import { AllocationRiskProfile } from '../../../expert-review/types/allocation.types'
import { getFundLogoPath, MockFundAmc } from '../../../../../data/mockUtils'
import { PLAN_MOCK_FUNDS, planMockFundIdentity, planMockFundWithLogo } from '../shared/planMockFunds'
import {
  LumpsumRecommendationActionType,
  LumpsumRecommendationStatus,
  LumpsumRecommendationSubType,
  LumpsumRecommendationType,
  LumpsumPlansPlanType,
  LumpsumPlanStatus,
  type LumpsumIdealAllocation
} from '../../../plans-v4/lumpsum/api/common.types'
import {
  LumpsumAllocationCategoryExecutionStatus,
  type LumpsumAllocationBuyRecommendation,
  type LumpsumAllocationCategory,
  type LumpsumAllocationCategoryAllocationMovement,
  type LumpsumAllocationCategoryFundMovement,
  type LumpsumAllocationExecutedRecommendation,
  type LumpsumAllocationInvalidatedRecommendation,
  type LumpsumAllocationNoActionFund,
  type LumpsumAllocationPlansResponse,
  type LumpsumAllocationSellRecommendation,
  type LumpsumAllocationUnderReviewRecommendation
} from '../../../plans-v4/lumpsum/api/allocation.types'
import { LumpsumAllocationInsightTag } from '../../../plans-v4/lumpsum/api/allocation.types'
import type { LumpsumIdealPlansResponse } from '../../../plans-v4/lumpsum/api/ideal.types'
import {
  type LumpsumRebalanceBuyRecommendation,
  type LumpsumRebalanceCategory,
  type LumpsumRebalanceExecutedRecommendation,
  type LumpsumRebalanceInvalidatedRecommendation,
  type LumpsumRebalancePlansResponse,
  type LumpsumRebalanceSellRecommendation,
  type LumpsumRebalanceUnderReviewRecommendation
} from '../../../plans-v4/lumpsum/api/rebalance.types'

function mockIdealAllocation(absolute: number, lowerLimit = absolute, upperLimit = absolute): LumpsumIdealAllocation {
  return { absolute, lowerLimit, upperLimit }
}

const MOCK_TRANSACTION_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'POST',
  navigationStyle: 'PUSH',
  url: '/v2/mutual-fund/execution/transaction',
  isNavigationDeferred: false
}

const MOCK_VIEW_REBALANCE_RISK_PROFILE_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'GET',
  navigationStyle: 'PUSH',
  url: '/v2/portfolio/rebalance-questionnaire/web-view-redirection?riskProfile=AGGRESSIVE',
  isNavigationDeferred: false
}

const MOCK_STRATEGY_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'GET',
  navigationStyle: 'PUSH',
  url: '/v2/mutual-fund/allocation/strategy-questionnaire',
  isNavigationDeferred: false
}

const MOCK_RESET_ALLOCATION_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'POST',
  navigationStyle: 'PUSH',
  url: '/v2/mutual-fund/allocation/reset-allocation',
  isNavigationDeferred: false
}

const MOCK_STRATEGY_LANDING_CTA = {
  type: 'CTA',
  ctaType: 'DEFAULT',
  method: 'GET',
  navigationStyle: 'PUSH',
  url: '/v2/allocation-strategy/landing'
}

const MOCK_SLOT_BOTTOMSHEET_CTA = {
  refreshScreen: false,
  url: '/v2/allocation-strategy/slot-bottom-sheet?planType=LUMP_SUM&group=placeholderGroup&category=placeholderCategory',
  method: 'GET'
}

const MOCK_CORE_BOTTOMSHEET_CTA = {
  refreshScreen: false,
  url: '/v2/allocation-strategy/segment-bottom-sheet?planType=LUMP_SUM&segment=CORE',
  method: 'GET'
}

function mockCategoryMovements({
  executionStatus,
  currentFundCount,
  idealFundCount,
  currentAllocationPercentage,
  idealAllocationAbsolute,
  idealAllocationLower = idealAllocationAbsolute,
  idealAllocationUpper = idealAllocationAbsolute,
  fundExecutionStatus = executionStatus,
  allocationExecutionStatus = executionStatus
}: {
  executionStatus: LumpsumAllocationCategoryExecutionStatus
  currentFundCount: number
  idealFundCount: number
  currentAllocationPercentage: number
  idealAllocationAbsolute: number
  idealAllocationLower?: number
  idealAllocationUpper?: number
  fundExecutionStatus?: LumpsumAllocationCategoryExecutionStatus
  allocationExecutionStatus?: LumpsumAllocationCategoryExecutionStatus
}): {
  group: string
  segment: string
  fundMovement: LumpsumAllocationCategoryFundMovement
  allocationMovement: LumpsumAllocationCategoryAllocationMovement
} {
  return {
    group: 'HIGH_RISK',
    segment: 'CORE',
    fundMovement: {
      executionStatus: fundExecutionStatus,
      currentFundCount,
      idealAllocationFundCount: {
        absolute: idealFundCount,
        minimum: 0,
        maximum: Math.max(idealFundCount, 5)
      }
    },
    allocationMovement: {
      executionStatus: allocationExecutionStatus,
      currentAllocationPercentage,
      idealAllocationPercentage: mockIdealAllocation(
        idealAllocationAbsolute,
        idealAllocationLower,
        idealAllocationUpper
      )
    }
  }
}

function buildAllocationSellRecommendation(
  overrides: Partial<LumpsumAllocationSellRecommendation> & { isin: string; fundName: string; amc: MockFundAmc }
): LumpsumAllocationSellRecommendation {
  const { amc, ...recommendationOverrides } = overrides

  return {
    mfLogoUrl: getFundLogoPath(amc),
    fundNature: 'Equity',
    fundCategory: 'Large Cap',
    purchaseMode: 'REGULAR',
    distributionStatus: 'GROWTH',
    currentValue: 58211.99099,
    powerRank: 31,
    powerRating: 'OFF_TRACK',
    taxCategory: 'EQUITY',
    actionType: LumpsumRecommendationActionType.SELL,
    recommendationType: LumpsumRecommendationType.ACTIONABLE,
    recommendationStatus: null,
    recommendationStatusReason: 'Needs diversification',
    recommendationStatusDisplayText: '',
    sellAmount: 58211.99099,
    currentAllocationPercentage: 7.2,
    idealAllocationPercentage: mockIdealAllocation(0),
    executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE,
    missedGains: 0,
    tags: [],
    ...recommendationOverrides
  }
}

function buildAllocationBuyRecommendation(
  overrides: Partial<LumpsumAllocationBuyRecommendation> & { isin: string; fundName: string; amc: MockFundAmc }
): LumpsumAllocationBuyRecommendation {
  const { amc, ...recommendationOverrides } = overrides

  return {
    mfLogoUrl: getFundLogoPath(amc),
    fundNature: 'Equity',
    fundCategory: 'Large Cap',
    purchaseMode: 'REGULAR',
    distributionStatus: 'GROWTH',
    currentValue: 50000.99099,
    powerRank: 31,
    powerRating: 'OFF_TRACK',
    taxCategory: 'EQUITY',
    actionType: LumpsumRecommendationActionType.BUY,
    recommendationType: LumpsumRecommendationType.ACTIONABLE,
    recommendationStatus: null,
    recommendationStatusReason: 'Better performing',
    recommendationStatusDisplayText: '',
    buyAmount: 35000.99099,
    currentAllocationPercentage: 4.1,
    idealAllocationPercentage: mockIdealAllocation(15.7),
    executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
    tags: [],
    ...recommendationOverrides
  }
}

function buildRebalanceSellRecommendation(
  overrides: Partial<LumpsumRebalanceSellRecommendation> & { isin: string; fundName: string }
): LumpsumRebalanceSellRecommendation {
  return {
    fundNature: 'Equity',
    fundCategory: 'Large Cap',
    purchaseMode: 'REGULAR',
    distributionStatus: 'GROWTH',
    currentValue: 58211.99099,
    powerRank: 31,
    powerRating: 'OFF_TRACK',
    taxCategory: 'EQUITY',
    actionType: LumpsumRecommendationActionType.SELL,
    recommendationType: LumpsumRecommendationType.ACTIONABLE,
    recommendationStatus: null,
    recommendationStatusReason: 'Needs diversification',
    recommendationStatusDisplayText: '',
    sellAmount: 58211.99099,
    underPerformance: false,
    highExposure: false,
    missedGains: 0,
    ...overrides
  }
}

function buildAllocationUnderReviewRecommendation(
  overrides: Partial<LumpsumAllocationUnderReviewRecommendation> & { isin: string; fundName: string; amc: MockFundAmc }
): LumpsumAllocationUnderReviewRecommendation {
  const { amc, ...recommendationOverrides } = overrides

  return {
    ...buildAllocationSellRecommendation({ isin: overrides.isin, fundName: overrides.fundName, amc }),
    recommendationType: LumpsumRecommendationType.UNDER_REVIEW,
    recommendationStatus: null,
    tags: [],
    ...recommendationOverrides
  }
}

function buildAllocationInvalidatedRecommendation(
  overrides: Partial<LumpsumAllocationInvalidatedRecommendation> & { isin: string; fundName: string; amc: MockFundAmc }
): LumpsumAllocationInvalidatedRecommendation {
  const { amc, ...recommendationOverrides } = overrides

  return {
    ...buildAllocationSellRecommendation({ isin: overrides.isin, fundName: overrides.fundName, amc }),
    recommendationType: LumpsumRecommendationType.INVALIDATED,
    recommendationStatus: LumpsumRecommendationStatus.INVALIDATED,
    recommendationSubType: LumpsumRecommendationSubType.INVALIDATED_ACTIONABLE,
    tags: [],
    ...recommendationOverrides
  }
}

function buildRebalanceUnderReviewRecommendation(
  overrides: Partial<LumpsumRebalanceUnderReviewRecommendation> & { isin: string; fundName: string }
): LumpsumRebalanceUnderReviewRecommendation {
  return {
    fundNature: 'Equity',
    fundCategory: 'Large Cap',
    purchaseMode: 'REGULAR',
    distributionStatus: 'GROWTH',
    currentValue: 58211.99099,
    powerRank: 31,
    powerRating: 'OFF_TRACK',
    taxCategory: 'EQUITY',
    actionType: LumpsumRecommendationActionType.SELL,
    recommendationType: LumpsumRecommendationType.UNDER_REVIEW,
    recommendationStatus: null,
    recommendationStatusReason: '',
    recommendationStatusDisplayText: '',
    underReviewReason: 'EXPERT_REVIEW',
    underReviewReasonText: 'Expert review in progress',
    underPerformance: false,
    highExposure: false,
    missedGains: 0,
    ...overrides
  }
}

function buildRebalanceInvalidatedRecommendation(
  overrides: Partial<LumpsumRebalanceInvalidatedRecommendation> & { isin: string; fundName: string }
): LumpsumRebalanceInvalidatedRecommendation {
  return {
    fundNature: 'Equity',
    fundCategory: 'Large Cap',
    purchaseMode: 'REGULAR',
    distributionStatus: 'GROWTH',
    currentValue: 58211.99099,
    powerRank: 31,
    powerRating: 'OFF_TRACK',
    taxCategory: 'EQUITY',
    actionType: LumpsumRecommendationActionType.SELL,
    recommendationType: LumpsumRecommendationType.INVALIDATED,
    recommendationStatus: LumpsumRecommendationStatus.INVALIDATED,
    recommendationSubType: LumpsumRecommendationSubType.INVALIDATED_ACTIONABLE,
    recommendationStatusReason: '',
    recommendationStatusDisplayText: '',
    underPerformance: false,
    highExposure: false,
    missedGains: 0,
    ...overrides
  }
}

function buildRebalanceExecutedRecommendation(
  overrides: Partial<LumpsumRebalanceExecutedRecommendation> & { isin: string; fundName: string }
): LumpsumRebalanceExecutedRecommendation {
  return {
    fundNature: 'Equity',
    fundCategory: 'Large Cap',
    purchaseMode: 'REGULAR',
    distributionStatus: 'GROWTH',
    currentValue: 58211.99099,
    powerRank: 31,
    powerRating: 'OFF_TRACK',
    taxCategory: 'EQUITY',
    actionType: LumpsumRecommendationActionType.SELL,
    recommendationType: LumpsumRecommendationType.EXECUTED,
    underPerformance: false,
    highExposure: false,
    missedGains: 0,
    ...overrides
  }
}

function buildRebalanceBuyRecommendation(
  overrides: Partial<LumpsumRebalanceBuyRecommendation> & { isin: string; fundName: string }
): LumpsumRebalanceBuyRecommendation {
  return {
    fundNature: 'Equity',
    fundCategory: 'Large Cap',
    purchaseMode: 'REGULAR',
    distributionStatus: 'GROWTH',
    powerRank: 31,
    powerRating: 'OFF_TRACK',
    taxCategory: 'EQUITY',
    actionType: LumpsumRecommendationActionType.BUY,
    recommendationType: LumpsumRecommendationType.ACTIONABLE,
    recommendationStatus: null,
    recommendationStatusReason: 'Better performing',
    recommendationStatusDisplayText: '',
    buyAmount: 35000.99099,
    currentValue: 50000.99099,
    underPerformance: false,
    highExposure: false,
    missedGains: 0,
    ...overrides
  }
}

export enum LumpsumPlansMockScenario {
  NORMAL = 'normal',
  UNDER_REVIEW_ONLY = 'under_review_only',
  EMPTY = 'empty',
  WITH_TAGS = 'with_tags'
}

const DAY_MS = 24 * 60 * 60 * 1000

function resolveMockTimeToPlanDrop(daysToPlanDrop: number): number {
  return Date.now() + daysToPlanDrop * DAY_MS
}

const MOCK_RECOMMENDATION_STATUS_DISPLAY_TEXT = {
  NEW_SELL: 'New: Fund added to plan',
  NEW_BUY: 'New: Fund added to plan',
  UPDATED_SELL: 'Updated: Better alternate fund found',
  UPDATED_BUY: 'Updated: Plan revised for better fit',
  SAME: 'Same as Previous Plan'
} as const

const EMPTY_ALLOCATION_BUCKET_ARRAYS = {
  sellRecommendations: [] as LumpsumAllocationSellRecommendation[],
  buyRecommendations: [] as LumpsumAllocationBuyRecommendation[],
  underReviewRecommendations: [] as LumpsumAllocationUnderReviewRecommendation[],
  executedRecommendations: [] as LumpsumAllocationExecutedRecommendation[],
  invalidatedRecommendations: [] as LumpsumAllocationInvalidatedRecommendation[],
  noActionFunds: [] as LumpsumAllocationNoActionFund[]
}

/**
 * NORMAL allocation list — intentional matrix of category + fund execution statuses.
 *
 * Category fund/allocation movement statuses covered:
 *   LARGE_CAP   fund↓  | alloc↓   (ACTIONABLE_DECREASE / ACTIONABLE_DECREASE)
 *   MID_CAP     fund↑  | alloc↑   (ACTIONABLE_INCREASE / ACTIONABLE_INCREASE)
 *   SMALL_CAP   fund … | alloc …  (IN_PROGRESS / IN_PROGRESS)
 *   FLEXI_CAP   fund ✓ | alloc ✓  (COMPLETED / COMPLETED)
 *   MULTI_CAP   fund ✓ | alloc ✓  (ALREADY_IDEAL / ALREADY_IDEAL)
 *   VALUE       fund↓  | alloc↑   (divergent: DECREASE / INCREASE)
 *
 * Fund executionStatus on sell/buy covers:
 *   ACTIONABLE_DECREASE | ACTIONABLE_INCREASE | IN_PROGRESS | COMPLETED | ALREADY_IDEAL
 */
const MOCK_ALLOCATION_CATEGORIES_NORMAL: LumpsumAllocationCategory[] = [
  // --- 1. Consolidation category (fund + allocation both decrease) ---
  {
    category: 'LARGE_CAP',
    derivedCategory: 'LARGE_CAP',
    categoryName: 'Large Cap',
    ...mockCategoryMovements({
      executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE,
      currentFundCount: 4,
      idealFundCount: 2,
      currentAllocationPercentage: 31.4,
      idealAllocationAbsolute: 15.7,
      idealAllocationLower: 15.7,
      idealAllocationUpper: 20.6
    }),
    sellRecommendations: [
      // sellReady → validations allow sell CTA
      buildAllocationSellRecommendation({
        ...planMockFundIdentity('sellReady'),
        fundCategory: 'Large Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE,
        sellAmount: 85211.99099,
        currentValue: 85211.99099,
        currentAllocationPercentage: 7.2,
        idealAllocationPercentage: mockIdealAllocation(0, 0, 0),
        missedGains: 50126.99099,
        tags: [
          LumpsumAllocationInsightTag.MISSED_GAINS,
          LumpsumAllocationInsightTag.POOR_PERFORMANCE,
          LumpsumAllocationInsightTag.TOO_MANY_FUNDS,
          LumpsumAllocationInsightTag.CONCENTRATION_RISK
        ]
      }),
      // sellRegular → regular plan bottom sheet
      buildAllocationSellRecommendation({
        ...planMockFundIdentity('sellRegular'),
        fundCategory: 'Large Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE,
        sellAmount: 42100.99099,
        currentValue: 85211.99099,
        currentAllocationPercentage: 7.2,
        idealAllocationPercentage: mockIdealAllocation(5.9, 3.4, 7.5),
        tags: [
          LumpsumAllocationInsightTag.HIGH_EXPOSURE,
          LumpsumAllocationInsightTag.INSIGNIFICANT_ALLOCATION,
          LumpsumAllocationInsightTag.REALLOCATED_PREFERENCE
        ]
      }),
      // Reverse sell → fund: still Full/Partial; alloc: ideal-range (default ready)
      buildAllocationSellRecommendation({
        isin: 'INF090I01239',
        fundName: 'Franklin India Bluechip Fund',
        amc: MockFundAmc.FRANKLIN_TEMPLETON,
        fundCategory: 'Large Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
        sellAmount: 12000.99099,
        currentValue: 45189.99099,
        currentAllocationPercentage: 5.1,
        idealAllocationPercentage: mockIdealAllocation(6.7, 4.2, 8.3),
        recommendationStatus: LumpsumRecommendationStatus.SAME,
        recommendationStatusDisplayText: MOCK_RECOMMENDATION_STATUS_DISPLAY_TEXT.SAME,
        tags: [LumpsumAllocationInsightTag.POOR_PERFORMANCE, LumpsumAllocationInsightTag.CATEGORY_NOT_RECOMMENDED]
      }),
      // Already-ideal sell → ideal-range trailer
      buildAllocationSellRecommendation({
        isin: 'INF247K01LC1',
        fundName: 'Motilal Oswal Large Cap Fund',
        amc: MockFundAmc.MOTILAL_OSWAL,
        fundCategory: 'Large Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ALREADY_IDEAL,
        sellAmount: 8000.99099,
        currentValue: 36000.99099,
        currentAllocationPercentage: 4.8,
        idealAllocationPercentage: mockIdealAllocation(5.9, 3.4, 7.5),
        recommendationStatus: LumpsumRecommendationStatus.SAME,
        recommendationStatusDisplayText: MOCK_RECOMMENDATION_STATUS_DISPLAY_TEXT.SAME,
        tags: [LumpsumAllocationInsightTag.INSIGNIFICANT_ALLOCATION]
      })
    ],
    buyRecommendations: [
      // buyPpfasGiftFundEnquiry → PPFAS GIFT fund investment enquiry bottom sheet (no execution call)
      buildAllocationBuyRecommendation({
        ...planMockFundIdentity('buyPpfasGiftFundEnquiry'),
        fundCategory: 'Large Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
        buyAmount: 50000.99099,
        currentValue: 0,
        currentAllocationPercentage: 0,
        idealAllocationPercentage: mockIdealAllocation(8.3, 6.7, 10.8),
        tags: [LumpsumAllocationInsightTag.IMPROVES_DIVERSIFICATION]
      }),
      // buyReady → post root transactionCta
      buildAllocationBuyRecommendation({
        ...planMockFundIdentity('buyReady'),
        fundCategory: 'Large Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
        buyAmount: 45000.99099,
        currentValue: 52000.99099,
        currentAllocationPercentage: 4.1,
        idealAllocationPercentage: mockIdealAllocation(10.8, 8.3, 12.4),
        tags: [
          LumpsumAllocationInsightTag.INCREASES_EXPOSURE,
          LumpsumAllocationInsightTag.IMPROVES_DIVERSIFICATION,
          LumpsumAllocationInsightTag.TOP_RANKED_FUND
        ]
      }),
      // buyDisabled → not-allowed bottom sheet
      buildAllocationBuyRecommendation({
        ...planMockFundIdentity('buyDisabled'),
        fundCategory: 'Large Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
        buyAmount: 24224.99099,
        currentValue: 0,
        currentAllocationPercentage: 0,
        idealAllocationPercentage: mockIdealAllocation(8.3, 6.7, 10.8),
        tags: [LumpsumAllocationInsightTag.IMPROVES_PERFORMANCE]
      }),
      // buyNotOnboarded → onboarding CTA
      buildAllocationBuyRecommendation({
        ...planMockFundIdentity('buyNotOnboarded'),
        fundCategory: 'Large Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE,
        buyAmount: 18500.99099,
        currentValue: 32000.99099,
        currentAllocationPercentage: 2.4,
        idealAllocationPercentage: mockIdealAllocation(3.4, 2.6, 5.9),
        powerRank: 2,
        powerRating: 'IN_FORM',
        recommendationStatus: LumpsumRecommendationStatus.SAME,
        recommendationStatusDisplayText: MOCK_RECOMMENDATION_STATUS_DISPLAY_TEXT.SAME,
        tags: [LumpsumAllocationInsightTag.TOP_RANKED_FUND, LumpsumAllocationInsightTag.IMPROVES_PERFORMANCE]
      })
    ],
    underReviewRecommendations: [
      buildAllocationUnderReviewRecommendation({
        isin: 'INF200K01RV1',
        fundName: 'Nippon India Large Cap Fund',
        amc: MockFundAmc.NIPPON_LIFE_INDIA,
        fundCategory: 'Large Cap',
        recommendationStatusReason: '₹7,087 paid in extra commissions',
        underReviewReason: 'SWITCH_STCG',
        underReviewReasonText:
          'Switching now would cost you more in STCG taxes. We’ll recommend a switch when its potential gains clearly outweigh the cost.',
        sellAmount: 35000.99099,
        currentValue: 45000.99099,
        currentAllocationPercentage: 3.2,
        idealAllocationPercentage: mockIdealAllocation(0, 0, 2.6),
        tags: [LumpsumAllocationInsightTag.PARTIAL_SELL_TAX]
      })
    ],
    executedRecommendations: [
      buildAllocationSellRecommendation({
        isin: 'INF179K01EX1',
        fundName: 'HDFC Large Cap Fund (Executed)',
        amc: MockFundAmc.HDFC,
        fundCategory: 'Large Cap',
        recommendationType: LumpsumRecommendationType.EXECUTED,
        executionStatus: LumpsumAllocationCategoryExecutionStatus.COMPLETED,
        sellAmount: 58211.99099,
        currentValue: 58211.99099,
        currentAllocationPercentage: 6.7,
        idealAllocationPercentage: mockIdealAllocation(0, 0, 0),
        tags: [LumpsumAllocationInsightTag.HIGH_EXPOSURE, LumpsumAllocationInsightTag.REALLOCATED_PREFERENCE]
      })
    ],
    invalidatedRecommendations: [
      buildAllocationInvalidatedRecommendation({
        isin: 'INF109K01IV1',
        fundName: 'HDFC Top 100 Fund',
        amc: MockFundAmc.HDFC,
        recommendationSubType: LumpsumRecommendationSubType.INVALIDATED_ACTIONABLE,
        recommendationStatusDisplayText:
          'Reason will come here. Why was it removed from recommendation in the new plan',
        currentValue: 62100.99099,
        tags: [
          LumpsumAllocationInsightTag.HIGH_EXPOSURE,
          LumpsumAllocationInsightTag.POOR_PERFORMANCE,
          LumpsumAllocationInsightTag.CATEGORY_NOT_RECOMMENDED
        ]
      })
    ],
    noActionFunds: []
  },

  // --- 2. Diversification category (fund + allocation both increase) ---
  {
    category: 'MID_CAP',
    derivedCategory: 'MID_CAP',
    categoryName: 'Mid Cap',
    ...mockCategoryMovements({
      executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
      currentFundCount: 1,
      idealFundCount: 3,
      currentAllocationPercentage: 3.4,
      idealAllocationAbsolute: 15.7,
      idealAllocationLower: 12.4,
      idealAllocationUpper: 18.1
    }),
    sellRecommendations: [
      buildAllocationSellRecommendation({
        isin: 'INF174K01LS2',
        fundName: 'Kotak Emerging Equity Fund',
        amc: MockFundAmc.KOTAK_MAHINDRA,
        fundCategory: 'Mid Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE,
        sellAmount: 18000.99099,
        currentValue: 42000.99099,
        currentAllocationPercentage: 2.1,
        idealAllocationPercentage: mockIdealAllocation(0, 0, 1.8),
        tags: [
          LumpsumAllocationInsightTag.POOR_PERFORMANCE,
          LumpsumAllocationInsightTag.HIGH_EXPOSURE,
          LumpsumAllocationInsightTag.CONCENTRATION_RISK
        ]
      }),
      // In-progress sell → ideal-range trailer
      buildAllocationSellRecommendation({
        isin: 'INF247K01MC1',
        fundName: 'Motilal Oswal Midcap Fund',
        amc: MockFundAmc.MOTILAL_OSWAL,
        fundCategory: 'Mid Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.IN_PROGRESS,
        sellAmount: 9000.99099,
        currentValue: 28000.99099,
        currentAllocationPercentage: 1.4,
        idealAllocationPercentage: mockIdealAllocation(2.6, 1.8, 3.4),
        tags: [LumpsumAllocationInsightTag.REALLOCATED_PREFERENCE]
      })
    ],
    buyRecommendations: [
      buildAllocationBuyRecommendation({
        isin: 'INF209K01MD1',
        fundName: 'SBI Magnum Midcap Fund',
        amc: MockFundAmc.SBI,
        fundCategory: 'Mid Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
        buyAmount: 22000.99099,
        currentValue: 18000.99099,
        currentAllocationPercentage: 1.5,
        idealAllocationPercentage: mockIdealAllocation(7.5, 5.9, 9.1),
        tags: [LumpsumAllocationInsightTag.INCREASES_EXPOSURE, LumpsumAllocationInsightTag.TOP_RANKED_FUND]
      }),
      buildAllocationBuyRecommendation({
        isin: 'INF109K01MD1',
        fundName: 'ICICI Prudential Midcap Fund',
        amc: MockFundAmc.ICICI_PRUDENTIAL,
        fundCategory: 'Mid Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
        buyAmount: 15000.99099,
        currentValue: 0,
        currentAllocationPercentage: 0,
        idealAllocationPercentage: mockIdealAllocation(7.5, 5.9, 9.1),
        tags: [
          LumpsumAllocationInsightTag.IMPROVES_DIVERSIFICATION,
          LumpsumAllocationInsightTag.IMPROVES_PERFORMANCE,
          LumpsumAllocationInsightTag.TOP_RANKED_FUND
        ]
      }),
      // Completed buy → ideal-range trailer
      buildAllocationBuyRecommendation({
        isin: 'INF846K01MD1',
        fundName: 'Axis Midcap Fund',
        amc: MockFundAmc.AXIS,
        fundCategory: 'Mid Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.COMPLETED,
        buyAmount: 10000.99099,
        currentValue: 25000.99099,
        currentAllocationPercentage: 3.4,
        idealAllocationPercentage: mockIdealAllocation(4.2, 3.4, 6.7),
        tags: [LumpsumAllocationInsightTag.INCREASES_EXPOSURE]
      })
    ],
    underReviewRecommendations: [
      buildAllocationUnderReviewRecommendation({
        isin: 'INF959K01LX2',
        fundName: 'Nippon India Growth Fund',
        amc: MockFundAmc.NIPPON_LIFE_INDIA,
        fundCategory: 'Mid Cap',
        recommendationStatusReason: '₹18,540 missed gains if switched after the lock-in period ends.',
        underReviewReason: 'EXIT_LOAD_WINDOW',
        underReviewReasonText: 'Fund is still within the exit load window.',
        sellAmount: 18000.99099,
        currentValue: 32000.99099,
        tags: [LumpsumAllocationInsightTag.PARTIAL_SELL_LOCKIN, LumpsumAllocationInsightTag.PARTIAL_SELL_TAX]
      })
    ],
    noActionFunds: [
      {
        isin: 'INF397K01NA1',
        mfLogoUrl: getFundLogoPath(MockFundAmc.MIRAE),
        fundName: 'Mirae Asset Midcap Fund',
        fundNature: 'Equity',
        fundCategory: 'Mid Cap',
        purchaseMode: 'REGULAR',
        distributionStatus: 'GROWTH',
        currentValue: 85211.99099,
        currentAllocationPercentage: 12.4,
        powerRank: 2,
        powerRating: 'IN_FORM',
        taxCategory: 'EQUITY',
        tags: [LumpsumAllocationInsightTag.HIGH_EXPOSURE, LumpsumAllocationInsightTag.NEAR_CONCENTRATION_RISK]
      }
    ],
    executedRecommendations: [],
    invalidatedRecommendations: []
  },

  // --- 3. In progress category (both movements) ---
  {
    category: 'SMALL_CAP',
    derivedCategory: 'SMALL_CAP',
    categoryName: 'Small Cap',
    ...mockCategoryMovements({
      executionStatus: LumpsumAllocationCategoryExecutionStatus.IN_PROGRESS,
      currentFundCount: 2,
      idealFundCount: 3,
      currentAllocationPercentage: 8.3,
      idealAllocationAbsolute: 12.4,
      idealAllocationLower: 10.8,
      idealAllocationUpper: 15.7
    }),
    sellRecommendations: [
      buildAllocationSellRecommendation({
        isin: 'INF200K01SC2',
        fundName: 'Nippon India Small Cap Fund',
        amc: MockFundAmc.NIPPON_LIFE_INDIA,
        fundCategory: 'Small Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.IN_PROGRESS,
        sellAmount: 18500.99099,
        currentValue: 42000.99099,
        currentAllocationPercentage: 3.5,
        idealAllocationPercentage: mockIdealAllocation(2.6, 1.8, 4.2),
        missedGains: 8400.99099,
        tags: [LumpsumAllocationInsightTag.MISSED_GAINS, LumpsumAllocationInsightTag.POOR_PERFORMANCE]
      }),
      buildAllocationSellRecommendation({
        isin: 'INF247K01SC1',
        fundName: 'Motilal Oswal Small Cap Fund',
        amc: MockFundAmc.MOTILAL_OSWAL,
        fundCategory: 'Small Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE,
        sellAmount: 16000.99099,
        currentValue: 31000.99099,
        currentAllocationPercentage: 2.8,
        idealAllocationPercentage: mockIdealAllocation(0, 0, 1.8),
        tags: [
          LumpsumAllocationInsightTag.TOO_MANY_FUNDS,
          LumpsumAllocationInsightTag.CATEGORY_NOT_RECOMMENDED,
          LumpsumAllocationInsightTag.INSIGNIFICANT_ALLOCATION
        ]
      })
    ],
    buyRecommendations: [
      buildAllocationBuyRecommendation({
        isin: 'INF209K01SC1',
        fundName: 'SBI Small Cap Fund',
        amc: MockFundAmc.SBI,
        fundCategory: 'Small Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.IN_PROGRESS,
        buyAmount: 15000.99099,
        currentValue: 12000.99099,
        currentAllocationPercentage: 1.2,
        idealAllocationPercentage: mockIdealAllocation(6.7, 4.2, 8.3),
        tags: [LumpsumAllocationInsightTag.IMPROVES_DIVERSIFICATION]
      }),
      buildAllocationBuyRecommendation({
        isin: 'INF109K01SC1',
        fundName: 'ICICI Prudential Smallcap Fund',
        amc: MockFundAmc.ICICI_PRUDENTIAL,
        fundCategory: 'Small Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
        buyAmount: 12000.99099,
        currentValue: 0,
        currentAllocationPercentage: 0,
        idealAllocationPercentage: mockIdealAllocation(6.7, 4.2, 8.3),
        tags: [LumpsumAllocationInsightTag.INCREASES_EXPOSURE, LumpsumAllocationInsightTag.IMPROVES_PERFORMANCE]
      })
    ],
    underReviewRecommendations: [],
    noActionFunds: [],
    executedRecommendations: [
      buildAllocationBuyRecommendation({
        isin: 'INF194K01SC1',
        fundName: 'DSP Small Cap Fund',
        amc: MockFundAmc.DSP,
        fundCategory: 'Small Cap',
        recommendationType: LumpsumRecommendationType.EXECUTED,
        executionStatus: LumpsumAllocationCategoryExecutionStatus.COMPLETED,
        buyAmount: 8000.99099,
        currentValue: 20000.99099,
        currentAllocationPercentage: 1.8,
        idealAllocationPercentage: mockIdealAllocation(4.2, 2.6, 5.9),
        tags: [LumpsumAllocationInsightTag.TOP_RANKED_FUND]
      })
    ],
    invalidatedRecommendations: [
      buildAllocationInvalidatedRecommendation({
        isin: 'INF846K01SC1',
        fundName: 'Axis Small Cap Fund',
        amc: MockFundAmc.AXIS,
        fundCategory: 'Small Cap',
        actionType: LumpsumRecommendationActionType.BUY,
        recommendationSubType: LumpsumRecommendationSubType.INVALIDATED_UNDER_REVIEW,
        recommendationStatusDisplayText: 'Fund no longer fits your risk profile.',
        currentValue: 7400.99099,
        tags: [LumpsumAllocationInsightTag.INCREASES_EXPOSURE, LumpsumAllocationInsightTag.IMPROVES_DIVERSIFICATION]
      })
    ]
  },

  // --- 4. Completed category ---
  {
    category: 'FLEXI_CAP',
    derivedCategory: 'FLEXI_CAP',
    categoryName: 'Flexi Cap',
    ...mockCategoryMovements({
      executionStatus: LumpsumAllocationCategoryExecutionStatus.COMPLETED,
      currentFundCount: 3,
      idealFundCount: 3,
      currentAllocationPercentage: 17.3,
      idealAllocationAbsolute: 17.3,
      idealAllocationLower: 15.7,
      idealAllocationUpper: 20.6
    }),
    ...EMPTY_ALLOCATION_BUCKET_ARRAYS,
    noActionFunds: [
      {
        isin: 'INF846K01FC1',
        mfLogoUrl: getFundLogoPath(MockFundAmc.AXIS),
        fundName: 'Axis Flexi Cap Fund',
        fundNature: 'Equity',
        fundCategory: 'Flexi Cap',
        purchaseMode: 'REGULAR',
        distributionStatus: 'GROWTH',
        currentValue: 42000.99099,
        currentAllocationPercentage: 5.2,
        powerRank: 3,
        powerRating: 'IN_FORM',
        taxCategory: 'EQUITY',
        tags: [LumpsumAllocationInsightTag.INSIGNIFICANT_ALLOCATION]
      }
    ],
    executedRecommendations: [
      // Buy completed — fund: ✓ Completed; alloc: pie + ideal absolute%
      buildAllocationBuyRecommendation({
        isin: 'INF209K01FC1',
        fundName: 'HDFC Large Cap Fund',
        amc: MockFundAmc.HDFC,
        fundCategory: 'Large Cap',
        recommendationType: LumpsumRecommendationType.EXECUTED,
        executionStatus: LumpsumAllocationCategoryExecutionStatus.COMPLETED,
        buyAmount: 85211.99099,
        currentValue: 85211.99099,
        currentAllocationPercentage: 10.8,
        idealAllocationPercentage: mockIdealAllocation(15.7, 12.4, 18.1),
        tags: [LumpsumAllocationInsightTag.IMPROVES_DIVERSIFICATION, LumpsumAllocationInsightTag.TOP_RANKED_FUND]
      }),
      // Full sell completed — current value ₹0 after exit
      buildAllocationSellRecommendation({
        isin: 'INF179K01FC1',
        fundName: 'HDFC Large Cap Fund',
        amc: MockFundAmc.HDFC,
        fundCategory: 'Large Cap',
        recommendationType: LumpsumRecommendationType.EXECUTED,
        executionStatus: LumpsumAllocationCategoryExecutionStatus.COMPLETED,
        sellAmount: 85211.99099,
        currentValue: 0,
        currentAllocationPercentage: 0,
        idealAllocationPercentage: mockIdealAllocation(0, 0, 0),
        tags: [LumpsumAllocationInsightTag.TOO_MANY_FUNDS, LumpsumAllocationInsightTag.HIGH_EXPOSURE]
      }),
      // Partial sell completed — residual holding
      buildAllocationSellRecommendation({
        isin: 'INF109K01FC1',
        fundName: 'HDFC Large Cap Fund',
        amc: MockFundAmc.HDFC,
        fundCategory: 'Large Cap',
        recommendationType: LumpsumRecommendationType.EXECUTED,
        executionStatus: LumpsumAllocationCategoryExecutionStatus.COMPLETED,
        sellAmount: 45110.99099,
        currentValue: 40101.99099,
        currentAllocationPercentage: 8.3,
        idealAllocationPercentage: mockIdealAllocation(15.7, 12.4, 18.1),
        tags: [LumpsumAllocationInsightTag.CONCENTRATION_RISK, LumpsumAllocationInsightTag.REALLOCATED_PREFERENCE]
      })
    ]
  },

  // --- 5. Already ideal category ---
  {
    category: 'MULTI_CAP',
    derivedCategory: 'MULTI_CAP',
    categoryName: 'Multi Cap',
    ...mockCategoryMovements({
      executionStatus: LumpsumAllocationCategoryExecutionStatus.ALREADY_IDEAL,
      currentFundCount: 2,
      idealFundCount: 2,
      currentAllocationPercentage: 16.2,
      idealAllocationAbsolute: 16.5,
      idealAllocationLower: 14.9,
      idealAllocationUpper: 18.1
    }),
    ...EMPTY_ALLOCATION_BUCKET_ARRAYS,
    noActionFunds: [
      {
        isin: 'INF209K01MC1',
        mfLogoUrl: getFundLogoPath(MockFundAmc.SBI),
        fundName: 'SBI Multicap Fund',
        fundNature: 'Equity',
        fundCategory: 'Multi Cap',
        purchaseMode: 'REGULAR',
        distributionStatus: 'GROWTH',
        currentValue: 28000.99099,
        currentAllocationPercentage: 8.1,
        powerRank: 4,
        powerRating: 'IN_FORM',
        taxCategory: 'EQUITY',
        missedGains: 12450,
        tags: [LumpsumAllocationInsightTag.MISSED_GAINS, LumpsumAllocationInsightTag.TOO_MANY_FUNDS]
      },
      {
        isin: 'INF109K01MC1',
        mfLogoUrl: getFundLogoPath(MockFundAmc.ICICI_PRUDENTIAL),
        fundName: 'ICICI Prudential Multicap Fund',
        fundNature: 'Equity',
        fundCategory: 'Multi Cap',
        purchaseMode: 'REGULAR',
        distributionStatus: 'GROWTH',
        currentValue: 85211.99099,
        currentAllocationPercentage: 12.4,
        powerRank: 2,
        powerRating: 'IN_FORM',
        taxCategory: 'EQUITY',
        tags: [LumpsumAllocationInsightTag.CONCENTRATION_RISK, LumpsumAllocationInsightTag.CATEGORY_NOT_RECOMMENDED]
      }
    ],
    sellRecommendations: [
      // Fund-level already ideal still possible as residual edge case
      buildAllocationSellRecommendation({
        isin: 'INF846K01MC1',
        fundName: 'Axis Multicap Fund',
        amc: MockFundAmc.AXIS,
        fundCategory: 'Multi Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ALREADY_IDEAL,
        sellAmount: 5000.99099,
        currentValue: 22000.99099,
        currentAllocationPercentage: 3.5,
        idealAllocationPercentage: mockIdealAllocation(3.4, 2.6, 4.2),
        tags: [LumpsumAllocationInsightTag.INSIGNIFICANT_ALLOCATION]
      })
    ],
    buyRecommendations: [
      buildAllocationBuyRecommendation({
        isin: 'INF194K01MC1',
        fundName: 'DSP Multicap Fund',
        amc: MockFundAmc.DSP,
        fundCategory: 'Multi Cap',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ALREADY_IDEAL,
        buyAmount: 4000.99099,
        currentValue: 15000.99099,
        currentAllocationPercentage: 2.5,
        idealAllocationPercentage: mockIdealAllocation(3.4, 2.6, 4.2),
        tags: [LumpsumAllocationInsightTag.IMPROVES_PERFORMANCE]
      })
    ]
  },

  // --- 6. Divergent category: fund decrease vs allocation increase ---
  {
    category: 'VALUE',
    derivedCategory: 'VALUE',
    categoryName: 'Value',
    ...mockCategoryMovements({
      executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE,
      fundExecutionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE,
      allocationExecutionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
      currentFundCount: 3,
      idealFundCount: 2,
      currentAllocationPercentage: 4.2,
      idealAllocationAbsolute: 10.8,
      idealAllocationLower: 8.3,
      idealAllocationUpper: 12.4
    }),
    sellRecommendations: [
      buildAllocationSellRecommendation({
        isin: 'INF179K01VL1',
        fundName: 'HDFC Value Fund',
        amc: MockFundAmc.HDFC,
        fundCategory: 'Value',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE,
        sellAmount: 25000.99099,
        currentValue: 40000.99099,
        currentAllocationPercentage: 2.5,
        idealAllocationPercentage: mockIdealAllocation(0, 0, 1.8),
        missedGains: 12500.99099,
        tags: [
          LumpsumAllocationInsightTag.MISSED_GAINS,
          LumpsumAllocationInsightTag.TOO_MANY_FUNDS,
          LumpsumAllocationInsightTag.CATEGORY_NOT_RECOMMENDED,
          LumpsumAllocationInsightTag.HIGH_EXPOSURE
        ]
      })
    ],
    buyRecommendations: [
      buildAllocationBuyRecommendation({
        isin: 'INF209K01VL1',
        fundName: 'SBI Contra Fund',
        amc: MockFundAmc.SBI,
        fundCategory: 'Value',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
        buyAmount: 30000.99099,
        currentValue: 10000.99099,
        currentAllocationPercentage: 1.2,
        idealAllocationPercentage: mockIdealAllocation(8.3, 6.7, 10.8),
        tags: [
          LumpsumAllocationInsightTag.INCREASES_EXPOSURE,
          LumpsumAllocationInsightTag.IMPROVES_DIVERSIFICATION,
          LumpsumAllocationInsightTag.IMPROVES_PERFORMANCE,
          LumpsumAllocationInsightTag.TOP_RANKED_FUND
        ]
      }),
      buildAllocationBuyRecommendation({
        isin: 'INF109K01VL1',
        fundName: 'ICICI Prudential Value Discovery Fund',
        amc: MockFundAmc.ICICI_PRUDENTIAL,
        fundCategory: 'Value',
        executionStatus: LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_INCREASE,
        buyAmount: 18000.99099,
        currentValue: 0,
        currentAllocationPercentage: 0,
        idealAllocationPercentage: mockIdealAllocation(5.9, 3.4, 7.5),
        tags: [LumpsumAllocationInsightTag.IMPROVES_DIVERSIFICATION, LumpsumAllocationInsightTag.TOP_RANKED_FUND]
      })
    ],
    underReviewRecommendations: [],
    noActionFunds: [],
    executedRecommendations: [],
    invalidatedRecommendations: []
  }
]

function allocationCategoryShell(category: LumpsumAllocationCategory): LumpsumAllocationCategory {
  return {
    category: category.category,
    derivedCategory: category.derivedCategory,
    categoryName: category.categoryName,
    fundMovement: category.fundMovement,
    allocationMovement: category.allocationMovement,
    ...EMPTY_ALLOCATION_BUCKET_ARRAYS
  }
}

function buildAllocationCategoriesUnderReviewOnly(
  categories: LumpsumAllocationCategory[]
): LumpsumAllocationCategory[] {
  return categories.map((category) => ({
    ...allocationCategoryShell(category),
    underReviewRecommendations: category.underReviewRecommendations ?? []
  }))
}

function buildAllocationCategoriesEmpty(categories: LumpsumAllocationCategory[]): LumpsumAllocationCategory[] {
  return categories.map((category) => allocationCategoryShell(category))
}

/** Keep movement-status matrix; stamp NEW/UPDATED tags on first category sells/buys. */
function buildAllocationCategoriesWithTags(categories: LumpsumAllocationCategory[]): LumpsumAllocationCategory[] {
  return categories.map((category, categoryIndex) => {
    if (categoryIndex !== 0) {
      return category
    }

    return {
      ...category,
      sellRecommendations: (category.sellRecommendations ?? []).map((recommendation, recommendationIndex) => {
        if (recommendationIndex === 0) {
          return {
            ...recommendation,
            recommendationStatus: LumpsumRecommendationStatus.NEW,
            recommendationStatusDisplayText: MOCK_RECOMMENDATION_STATUS_DISPLAY_TEXT.NEW_SELL
          }
        }
        if (recommendationIndex === 1) {
          return {
            ...recommendation,
            recommendationStatus: LumpsumRecommendationStatus.UPDATED,
            recommendationStatusDisplayText: MOCK_RECOMMENDATION_STATUS_DISPLAY_TEXT.UPDATED_SELL
          }
        }
        if (recommendationIndex === 2) {
          return {
            ...recommendation,
            recommendationStatus: LumpsumRecommendationStatus.SAME,
            recommendationStatusDisplayText: MOCK_RECOMMENDATION_STATUS_DISPLAY_TEXT.SAME
          }
        }
        return recommendation
      }),
      buyRecommendations: (category.buyRecommendations ?? []).map((recommendation, recommendationIndex) => {
        if (recommendationIndex === 0) {
          return {
            ...recommendation,
            recommendationStatus: LumpsumRecommendationStatus.NEW,
            recommendationStatusDisplayText: MOCK_RECOMMENDATION_STATUS_DISPLAY_TEXT.NEW_BUY
          }
        }
        if (recommendationIndex === 1) {
          return {
            ...recommendation,
            recommendationStatus: LumpsumRecommendationStatus.UPDATED,
            recommendationStatusDisplayText: MOCK_RECOMMENDATION_STATUS_DISPLAY_TEXT.UPDATED_BUY
          }
        }
        if (recommendationIndex === 2) {
          return {
            ...recommendation,
            recommendationStatus: LumpsumRecommendationStatus.SAME,
            recommendationStatusDisplayText: MOCK_RECOMMENDATION_STATUS_DISPLAY_TEXT.SAME
          }
        }
        return recommendation
      })
    }
  })
}

/** Current value matches ideal, and ideal funds have no buy or sell left. */
function applyIdealPlansTopShape(plans: LumpsumIdealPlansResponse): LumpsumIdealPlansResponse {
  return {
    ...plans,
    currentPortfolioValue: plans.idealPortfolioValue,
    categories: (plans.categories ?? []).map((category) => ({
      ...category,
      idealFunds: (category.idealFunds ?? []).map((fund) => ({
        ...fund,
        buyAmount: null,
        sellAmount: null
      }))
    }))
  }
}

/** Rolled-out list with no sell/buy, so headers treat the portfolio as already in top shape. */
function applyAllocationCategoriesTopShape(
  categories: LumpsumAllocationCategory[]
): LumpsumAllocationCategory[] {
  return categories.map((category) => ({
    ...category,
    fundMovement: {
      ...category.fundMovement,
      executionStatus: LumpsumAllocationCategoryExecutionStatus.ALREADY_IDEAL
    },
    allocationMovement: {
      ...category.allocationMovement,
      executionStatus: LumpsumAllocationCategoryExecutionStatus.ALREADY_IDEAL
    },
    sellRecommendations: [],
    buyRecommendations: []
  }))
}

function getAllocationCategoriesForScenario(
  scenario: LumpsumPlansMockScenario,
  isTopShape: boolean
): LumpsumAllocationCategory[] {
  let categories = MOCK_ALLOCATION_CATEGORIES_NORMAL
  if (scenario === LumpsumPlansMockScenario.UNDER_REVIEW_ONLY) {
    categories = buildAllocationCategoriesUnderReviewOnly(MOCK_ALLOCATION_CATEGORIES_NORMAL)
  } else if (scenario === LumpsumPlansMockScenario.EMPTY) {
    categories = buildAllocationCategoriesEmpty(MOCK_ALLOCATION_CATEGORIES_NORMAL)
  } else if (scenario === LumpsumPlansMockScenario.WITH_TAGS) {
    categories = buildAllocationCategoriesWithTags(MOCK_ALLOCATION_CATEGORIES_NORMAL)
  }

  if (!isTopShape) {
    return categories
  }
  return applyAllocationCategoriesTopShape(categories)
}

function countAllocationUnderReviewFunds(categories: LumpsumAllocationCategory[]): number {
  return categories.reduce((fundCount, category) => fundCount + (category.underReviewRecommendations?.length ?? 0), 0)
}

function countAllocationSellFunds(categories: LumpsumAllocationCategory[]): number {
  return categories.reduce((fundCount, category) => fundCount + (category.sellRecommendations?.length ?? 0), 0)
}

function countAllocationBuyFunds(categories: LumpsumAllocationCategory[]): number {
  return categories.reduce((fundCount, category) => fundCount + (category.buyRecommendations?.length ?? 0), 0)
}

function countAllocationInvalidatedFunds(categories: LumpsumAllocationCategory[]): number {
  return categories.reduce((fundCount, category) => fundCount + (category.invalidatedRecommendations?.length ?? 0), 0)
}

function countAllocationActionableFunds(categories: LumpsumAllocationCategory[]): number {
  return categories.reduce((fundCount, category) => {
    const sellCount = category.sellRecommendations?.length ?? 0
    const buyCount = category.buyRecommendations?.length ?? 0
    return fundCount + sellCount + buyCount
  }, 0)
}

function countRebalanceActionableFunds(categories: LumpsumRebalanceCategory[]): number {
  return categories.reduce((fundCount, category) => {
    const sellCount = category.sellRecommendations?.length ?? 0
    const buyCount = category.buyRecommendations?.length ?? 0
    return fundCount + sellCount + buyCount
  }, 0)
}

function countRebalanceUnderReviewFunds(categories: LumpsumRebalanceCategory[]): number {
  return categories.reduce((fundCount, category) => fundCount + (category.underReviewRecommendations?.length ?? 0), 0)
}

function countRebalanceSellFunds(categories: LumpsumRebalanceCategory[]): number {
  return categories.reduce((fundCount, category) => fundCount + (category.sellRecommendations?.length ?? 0), 0)
}

function countRebalanceBuyFunds(categories: LumpsumRebalanceCategory[]): number {
  return categories.reduce((fundCount, category) => fundCount + (category.buyRecommendations?.length ?? 0), 0)
}

function countRebalanceInvalidatedFunds(categories: LumpsumRebalanceCategory[]): number {
  return categories.reduce((fundCount, category) => fundCount + (category.invalidatedRecommendations?.length ?? 0), 0)
}

function rebalanceBuyFlagsFromTags(tags?: LumpsumAllocationInsightTag[] | null): {
  underPerformance: boolean
  highExposure: boolean
} {
  const insightTags = tags ?? []
  return {
    underPerformance:
      insightTags.includes(LumpsumAllocationInsightTag.IMPROVES_PERFORMANCE) ||
      insightTags.includes(LumpsumAllocationInsightTag.TOP_RANKED_FUND),
    highExposure:
      insightTags.includes(LumpsumAllocationInsightTag.INCREASES_EXPOSURE) ||
      insightTags.includes(LumpsumAllocationInsightTag.IMPROVES_DIVERSIFICATION)
  }
}

function rebalanceSellFlagsFromTags(tags?: LumpsumAllocationInsightTag[] | null): {
  underPerformance: boolean
  highExposure: boolean
} {
  const insightTags = tags ?? []
  return {
    underPerformance: insightTags.includes(LumpsumAllocationInsightTag.POOR_PERFORMANCE),
    highExposure:
      insightTags.includes(LumpsumAllocationInsightTag.HIGH_EXPOSURE) ||
      insightTags.includes(LumpsumAllocationInsightTag.CONCENTRATION_RISK) ||
      insightTags.includes(LumpsumAllocationInsightTag.NEAR_CONCENTRATION_RISK)
  }
}

/** Tag flags fill every fund. The ISIN map only overrides a demo fund. */
function resolveRebalanceBuyInsight(tags: LumpsumAllocationInsightTag[] | null | undefined, isin: string): {
  underPerformance: boolean
  highExposure: boolean
} {
  const fromTags = rebalanceBuyFlagsFromTags(tags)
  const fromIsin = rebalanceBuyInsightByIsin(isin)
  return {
    underPerformance: fromIsin.underPerformance ?? fromTags.underPerformance,
    highExposure: fromIsin.highExposure ?? fromTags.highExposure
  }
}

function resolveRebalanceSellInsight(
  tags: LumpsumAllocationInsightTag[] | null | undefined,
  isin: string,
  missedGains?: number | null
): {
  underPerformance: boolean
  highExposure: boolean
  missedGains: number
} {
  const fromTags = rebalanceSellFlagsFromTags(tags)
  const fromIsin = rebalanceSellInsightByIsin(isin)
  return {
    underPerformance: fromIsin.underPerformance ?? fromTags.underPerformance,
    highExposure: fromIsin.highExposure ?? fromTags.highExposure,
    missedGains: fromIsin.missedGains ?? missedGains ?? 0
  }
}

function rebalanceSellInsightByIsin(isin: string): {
  underPerformance?: boolean
  highExposure?: boolean
  missedGains?: number
} {
  if (isin === PLAN_MOCK_FUNDS.sellRegular.isin) return { highExposure: true }
  if (isin === PLAN_MOCK_FUNDS.sellReady.isin) return { missedGains: 12000.99099 }
  if (isin === 'INF174K01LS2') return { underPerformance: true }
  if (isin === 'INF789K01UT1') return { missedGains: 5600.99099 }
  if (isin === 'INF397K01MR1') return { missedGains: 2100.99099, underPerformance: true }
  return {}
}

function rebalanceBuyInsightByIsin(isin: string): {
  underPerformance?: boolean
  highExposure?: boolean
} {
  if (isin === PLAN_MOCK_FUNDS.buyDisabled.isin) return { underPerformance: true }
  if (isin === 'INF209K01YH1') return { highExposure: true }
  if (isin === 'INF109K01SC1') return { underPerformance: true }
  if (isin === PLAN_MOCK_FUNDS.buyNotOnboarded.isin) return { underPerformance: true }
  return {}
}

function rebalanceUnderReviewInsightByIsin(isin: string): { missedGains?: number } {
  if (isin === 'INF200K01RV1') return { missedGains: 7087.99099 }
  if (isin === 'INF959K01LX2') return { missedGains: 18540.99099 }
  return {}
}

function buildRebalanceCategoriesFromAllocation(
  allocationCategories: LumpsumAllocationCategory[]
): LumpsumRebalanceCategory[] {
  return allocationCategories.map((category) => ({
    category: category.category,
    categoryName: category.categoryName,
    sellRecommendations: (category.sellRecommendations ?? []).map((recommendation) => {
      const isin = recommendation.isin ?? ''
      const sellInsight = resolveRebalanceSellInsight(recommendation.tags, isin, recommendation.missedGains)
      return buildRebalanceSellRecommendation({
        isin,
        fundName: recommendation.fundName ?? '',
        mfLogoUrl: recommendation.mfLogoUrl,
        fundNature: recommendation.fundNature,
        fundCategory: recommendation.fundCategory,
        purchaseMode: recommendation.purchaseMode,
        distributionStatus: recommendation.distributionStatus,
        currentValue: recommendation.currentValue,
        powerRank: recommendation.powerRank,
        powerRating: recommendation.powerRating,
        taxCategory: recommendation.taxCategory,
        actionType: recommendation.actionType,
        recommendationType: recommendation.recommendationType,
        recommendationStatus: recommendation.recommendationStatus,
        recommendationStatusReason: recommendation.recommendationStatusReason,
        recommendationStatusDisplayText: recommendation.recommendationStatusDisplayText ?? '',
        sellAmount: recommendation.sellAmount,
        underPerformance: sellInsight.underPerformance,
        highExposure: sellInsight.highExposure,
        missedGains: sellInsight.missedGains
      })
    }),
    buyRecommendations: (category.buyRecommendations ?? []).map((recommendation) => {
      const isin = recommendation.isin ?? ''
      const buyInsight = resolveRebalanceBuyInsight(recommendation.tags, isin)
      return buildRebalanceBuyRecommendation({
        isin,
        fundName: recommendation.fundName ?? '',
        mfLogoUrl: recommendation.mfLogoUrl,
        fundNature: recommendation.fundNature,
        fundCategory: recommendation.fundCategory,
        purchaseMode: recommendation.purchaseMode,
        distributionStatus: recommendation.distributionStatus,
        powerRank: recommendation.powerRank,
        powerRating: recommendation.powerRating,
        taxCategory: recommendation.taxCategory,
        actionType: recommendation.actionType,
        recommendationType: recommendation.recommendationType,
        recommendationStatus: recommendation.recommendationStatus,
        recommendationStatusReason: recommendation.recommendationStatusReason,
        recommendationStatusDisplayText: recommendation.recommendationStatusDisplayText ?? '',
        buyAmount: recommendation.buyAmount,
        currentValue: recommendation?.currentValue ?? 0,
        underPerformance: buyInsight.underPerformance,
        highExposure: buyInsight.highExposure,
        missedGains: 0
      })
    }),
    underReviewRecommendations: (category.underReviewRecommendations ?? []).map((recommendation) => {
      const isin = recommendation.isin ?? ''
      const underReviewInsight = rebalanceUnderReviewInsightByIsin(isin)
      return buildRebalanceUnderReviewRecommendation({
        isin,
        fundName: recommendation.fundName ?? '',
        mfLogoUrl: recommendation.mfLogoUrl,
        fundNature: recommendation.fundNature,
        fundCategory: recommendation.fundCategory,
        purchaseMode: recommendation.purchaseMode,
        distributionStatus: recommendation.distributionStatus,
        currentValue: recommendation.currentValue,
        powerRank: recommendation.powerRank,
        powerRating: recommendation.powerRating,
        taxCategory: recommendation.taxCategory,
        actionType: recommendation.actionType,
        recommendationStatus: recommendation.recommendationStatus,
        recommendationStatusReason: recommendation.recommendationStatusReason,
        recommendationStatusDisplayText: recommendation.recommendationStatusDisplayText ?? '',
        underReviewReason: recommendation.underReviewReason,
        underReviewReasonText: recommendation.underReviewReasonText,
        missedGains: underReviewInsight.missedGains ?? 0
      })
    }),
    executedRecommendations: (category.executedRecommendations ?? []).map((recommendation) => {
      const isin = recommendation.isin ?? ''
      const isBuy = recommendation.actionType === LumpsumRecommendationActionType.BUY
      const sellInsight = resolveRebalanceSellInsight(recommendation.tags, isin, recommendation.missedGains)
      const buyInsight = resolveRebalanceBuyInsight(recommendation.tags, isin)

      return buildRebalanceExecutedRecommendation({
        isin,
        fundName: recommendation.fundName ?? '',
        mfLogoUrl: recommendation.mfLogoUrl,
        fundNature: recommendation.fundNature,
        fundCategory: recommendation.fundCategory,
        purchaseMode: recommendation.purchaseMode,
        distributionStatus: recommendation.distributionStatus,
        currentValue: recommendation.currentValue,
        powerRank: recommendation.powerRank,
        powerRating: recommendation.powerRating,
        taxCategory: recommendation.taxCategory,
        actionType: recommendation.actionType,
        recommendationType: recommendation.recommendationType,
        underPerformance: isBuy ? buyInsight.underPerformance : sellInsight.underPerformance,
        highExposure: isBuy ? buyInsight.highExposure : sellInsight.highExposure,
        missedGains: isBuy ? 0 : sellInsight.missedGains
      })
    }),
    invalidatedRecommendations: (category.invalidatedRecommendations ?? []).map((recommendation) => {
      const isin = recommendation.isin ?? ''
      const isBuy = recommendation.actionType === LumpsumRecommendationActionType.BUY
      const sellInsight = resolveRebalanceSellInsight(recommendation.tags, isin, recommendation.missedGains)
      const buyInsight = resolveRebalanceBuyInsight(recommendation.tags, isin)

      return buildRebalanceInvalidatedRecommendation({
        isin,
        fundName: recommendation.fundName ?? '',
        mfLogoUrl: recommendation.mfLogoUrl,
        fundNature: recommendation.fundNature,
        fundCategory: recommendation.fundCategory,
        purchaseMode: recommendation.purchaseMode,
        distributionStatus: recommendation.distributionStatus,
        currentValue: recommendation.currentValue,
        powerRank: recommendation.powerRank,
        powerRating: recommendation.powerRating,
        taxCategory: recommendation.taxCategory,
        actionType: recommendation.actionType,
        recommendationSubType: recommendation.recommendationSubType,
        recommendationStatus: recommendation.recommendationStatus,
        recommendationStatusReason: recommendation.recommendationStatusReason ?? '',
        recommendationStatusDisplayText: recommendation.recommendationStatusDisplayText ?? '',
        underPerformance: isBuy ? buyInsight.underPerformance : sellInsight.underPerformance,
        highExposure: isBuy ? buyInsight.highExposure : sellInsight.highExposure,
        missedGains: isBuy ? 0 : sellInsight.missedGains
      })
    }),
    noActionFunds: (category.noActionFunds ?? []).map((fund) => ({
      isin: fund.isin,
      mfLogoUrl: fund.mfLogoUrl,
      fundName: fund.fundName,
      fundNature: fund.fundNature,
      fundCategory: fund.fundCategory,
      purchaseMode: fund.purchaseMode,
      distributionStatus: fund.distributionStatus,
      currentValue: fund.currentValue,
      currentAllocationPercentage: fund.currentAllocationPercentage,
      powerRank: fund.powerRank,
      powerRating: fund.powerRating,
      taxCategory: fund.taxCategory
    }))
  }))
}

function getMockPlanCountForScenario(scenario: LumpsumPlansMockScenario): number {
  if (scenario === LumpsumPlansMockScenario.WITH_TAGS) {
    return 2
  }
  return 1
}

function getMockPlanSnapshotDateForScenario(scenario: LumpsumPlansMockScenario): number {
  if (scenario === LumpsumPlansMockScenario.WITH_TAGS) {
    return Date.now() - 2 * 24 * 60 * 60 * 1000
  }
  return 1685577600000
}

export type LumpsumPlanGateMockConfig = {
  planStatus: LumpsumPlanStatus
  isPlanSubscriptionEligible: boolean
  daysToPlanDrop: number
  /** Rolled-out portfolio with sell and buy cleared. */
  isTopShape: boolean
}

const DEFAULT_PLAN_GATE: LumpsumPlanGateMockConfig = {
  planStatus: LumpsumPlanStatus.PLAN_ROLLED_OUT,
  isPlanSubscriptionEligible: true,
  daysToPlanDrop: 7,
  isTopShape: false
}

export function mockLumpsumRebalancePlans(
  scenario: LumpsumPlansMockScenario = LumpsumPlansMockScenario.NORMAL,
  planGate: LumpsumPlanGateMockConfig = DEFAULT_PLAN_GATE
): LumpsumRebalancePlansResponse {
  const { planStatus, isPlanSubscriptionEligible, daysToPlanDrop, isTopShape } = planGate
  const listScenario = resolveListScenarioForPlanStatus(planStatus, scenario)
  const allocationCategories = getAllocationCategoriesForScenario(listScenario, isTopShape)
  const categories = buildRebalanceCategoriesFromAllocation(allocationCategories)
  const showPlanUpdateTags = listScenario === LumpsumPlansMockScenario.WITH_TAGS && !isTopShape

  return {
    userId: 'mock-user',
    planType: LumpsumPlansPlanType.REBALANCE,
    timeToPlanDrop: resolveMockTimeToPlanDrop(daysToPlanDrop),
    // Feature toggle (coming-soon when false). Always on in mocks.
    isPlanEnabled: true,
    // Rebalance has no planStatus — list vs timer is gated by isPlanAvailable.
    isPlanAvailable: planStatus === LumpsumPlanStatus.PLAN_ROLLED_OUT,
    isPlanSubscriptionEligible,
    planCount: getMockPlanCountForScenario(listScenario),
    transactionCta: MOCK_TRANSACTION_CTA,
    viewRebalanceRiskProfileCta: MOCK_VIEW_REBALANCE_RISK_PROFILE_CTA,
    actionableFundCount: countRebalanceActionableFunds(categories),
    underReviewFundCount: countRebalanceUnderReviewFunds(categories),
    invalidatedFundCount: countRebalanceInvalidatedFunds(categories),
    sellFundCount: countRebalanceSellFunds(categories),
    buyFundCount: countRebalanceBuyFunds(categories),
    missedGains: listScenario === LumpsumPlansMockScenario.EMPTY || isTopShape ? 0 : 45334.99099,
    newRecommendationsCount: showPlanUpdateTags ? 4 : null,
    updatedRecommendationsCount: showPlanUpdateTags ? 2 : null,
    invalidatedRecommendationsCount: showPlanUpdateTags ? 1 : null,
    planSnapshotDate: getMockPlanSnapshotDateForScenario(listScenario),
    planDurationInMonths: 2,
    riskProfile: AllocationRiskProfile.VERY_AGGRESSIVE,
    categories
  }
}

function resolveListScenarioForPlanStatus(
  planStatus: LumpsumPlanStatus,
  scenario: LumpsumPlansMockScenario
): LumpsumPlansMockScenario {
  // Timer / strategy shells do not show a fund list.
  if (planStatus !== LumpsumPlanStatus.PLAN_ROLLED_OUT) {
    return LumpsumPlansMockScenario.EMPTY
  }
  return scenario
}

export function mockLumpsumAllocationPlans(
  scenario: LumpsumPlansMockScenario = LumpsumPlansMockScenario.NORMAL,
  planGate: LumpsumPlanGateMockConfig = DEFAULT_PLAN_GATE
): LumpsumAllocationPlansResponse {
  const { planStatus, isPlanSubscriptionEligible, daysToPlanDrop, isTopShape } = planGate
  const listScenario = resolveListScenarioForPlanStatus(planStatus, scenario)
  const categories = getAllocationCategoriesForScenario(listScenario, isTopShape)
  const showPlanUpdateTags = listScenario === LumpsumPlansMockScenario.WITH_TAGS && !isTopShape

  return {
    userId: 'mock-user',
    planType: LumpsumPlansPlanType.ALLOCATION,
    timeToPlanDrop: resolveMockTimeToPlanDrop(daysToPlanDrop),
    isPlanEnabled: true,
    isPlanSubscriptionEligible,
    planStatus,
    planCount: getMockPlanCountForScenario(listScenario),
    transactionCta: MOCK_TRANSACTION_CTA,
    actionableFundCount: countAllocationActionableFunds(categories),
    underReviewFundCount: countAllocationUnderReviewFunds(categories),
    invalidatedFundCount: countAllocationInvalidatedFunds(categories),
    sellFundCount: countAllocationSellFunds(categories),
    buyFundCount: countAllocationBuyFunds(categories),
    includedFundCount: listScenario === LumpsumPlansMockScenario.EMPTY ? 0 : 5,
    idealFundCount: listScenario === LumpsumPlansMockScenario.EMPTY ? 0 : 6,
    newRecommendationsCount: showPlanUpdateTags ? 4 : null,
    updatedRecommendationsCount: showPlanUpdateTags ? 2 : null,
    invalidatedRecommendationsCount: showPlanUpdateTags ? 1 : null,
    planSnapshotDate: getMockPlanSnapshotDateForScenario(listScenario),
    planDurationInMonths: 2,
    excludedFundCount: listScenario === LumpsumPlansMockScenario.EMPTY ? 0 : 4,
    riskProfile: AllocationRiskProfile.VERY_AGGRESSIVE,
    strategyCta: MOCK_STRATEGY_CTA,
    resetAllocation: MOCK_RESET_ALLOCATION_CTA,
    strategyLandingCta: MOCK_STRATEGY_LANDING_CTA,
    slotBottomsheetCta: MOCK_SLOT_BOTTOMSHEET_CTA,
    coreBottomsheetCta: MOCK_CORE_BOTTOMSHEET_CTA,
    categories
  }
}

export function mockLumpsumIdealPlans(
  scenario: LumpsumPlansMockScenario = LumpsumPlansMockScenario.NORMAL,
  planGate: LumpsumPlanGateMockConfig = DEFAULT_PLAN_GATE
): LumpsumIdealPlansResponse {
  const { planStatus, isPlanSubscriptionEligible, daysToPlanDrop, isTopShape } = planGate
  const listScenario = resolveListScenarioForPlanStatus(planStatus, scenario)

  if (listScenario === LumpsumPlansMockScenario.EMPTY) {
    return {
      userId: 'mock-user',
      planType: LumpsumPlansPlanType.ALLOCATION,
      timeToPlanDrop: resolveMockTimeToPlanDrop(daysToPlanDrop),
      isPlanEnabled: true,
      isPlanSubscriptionEligible,
      planStatus,
      idealFundCount: 0,
      currentPortfolioValue: 0,
      idealPortfolioValue: 0,
      allocationPlanSnapshotDate: getMockPlanSnapshotDateForScenario(listScenario),
      planDurationInMonths: 2,
      planCount: 0,
      strategyCta: MOCK_STRATEGY_CTA,
      resetAllocation: MOCK_RESET_ALLOCATION_CTA,
      strategyLandingCta: MOCK_STRATEGY_LANDING_CTA,
      riskProfile: AllocationRiskProfile.VERY_AGGRESSIVE,
      categories: []
    }
  }

  const idealPlans: LumpsumIdealPlansResponse = {
    userId: 'mock-user',
    planType: LumpsumPlansPlanType.ALLOCATION,
    timeToPlanDrop: resolveMockTimeToPlanDrop(daysToPlanDrop),
    isPlanEnabled: true,
    isPlanSubscriptionEligible,
    planStatus,
    idealFundCount: 9,
    currentPortfolioValue: 997210.99099,
    idealPortfolioValue: 1000000.99099,
    allocationPlanSnapshotDate: 1685577600000,
    planDurationInMonths: 2,
    planCount: 3,
    transactionCta: MOCK_TRANSACTION_CTA,
    strategyCta: MOCK_STRATEGY_CTA,
    resetAllocation: MOCK_RESET_ALLOCATION_CTA,
    strategyLandingCta: MOCK_STRATEGY_LANDING_CTA,
    riskProfile: AllocationRiskProfile.VERY_AGGRESSIVE,
    categories: [
      {
        categoryId: 'LARGE_CAP',
        categoryName: 'Large Cap',
        idealAllocationPercentage: { absolute: 17.3, lowerLimit: 15.7, upperLimit: 20.6 },
        idealFunds: [
          {
            ...planMockFundWithLogo('buyReady'),
            fundNature: 'Equity',
            fundCategory: 'Large Cap',
            purchaseMode: 'REGULAR',
            distributionStatus: 'GROWTH',
            currentValue: 55000.99099,
            powerRank: 2,
            powerRating: 'IN_FORM',
            taxCategory: 'EQUITY',
            idealAllocationPercentage: { absolute: 7.5, lowerLimit: 5.9, upperLimit: 10.8 },
            buyAmount: 20000.99099,
            sellAmount: null
          },
          {
            ...planMockFundWithLogo('buyDisabled'),
            fundNature: 'Equity',
            fundCategory: 'Large Cap',
            purchaseMode: 'REGULAR',
            distributionStatus: 'GROWTH',
            currentValue: 0,
            powerRank: 3,
            powerRating: 'IN_FORM',
            taxCategory: 'EQUITY',
            idealAllocationPercentage: { absolute: 6.2, lowerLimit: 4.2, upperLimit: 8.3 },
            buyAmount: 45000.99099,
            sellAmount: null
          },
          {
            ...planMockFundWithLogo('buyNotOnboarded'),
            fundNature: 'Equity',
            fundCategory: 'Large Cap',
            purchaseMode: 'REGULAR',
            distributionStatus: 'GROWTH',
            currentValue: 0,
            powerRank: 1,
            powerRating: 'IN_FORM',
            taxCategory: 'EQUITY',
            idealAllocationPercentage: { absolute: 4.8, lowerLimit: 3.4, upperLimit: 6.7 },
            buyAmount: 18500.99099,
            sellAmount: null
          }
        ]
      },
      {
        categoryId: 'MID_CAP',
        categoryName: 'Mid Cap',
        idealAllocationPercentage: { absolute: 15.7, lowerLimit: 12.4, upperLimit: 18.1 },
        idealFunds: [
          {
            isin: 'INF174K01LS2',
            mfLogoUrl: getFundLogoPath(MockFundAmc.KOTAK_MAHINDRA),
            fundName: 'Kotak Emerging Equity Fund',
            fundNature: 'Equity',
            fundCategory: 'Mid Cap',
            purchaseMode: 'REGULAR',
            distributionStatus: 'GROWTH',
            currentValue: 32000.99099,
            powerRank: 2,
            powerRating: 'IN_FORM',
            taxCategory: 'EQUITY',
            idealAllocationPercentage: { absolute: 4.5, lowerLimit: 3.2, upperLimit: 6.1 },
            buyAmount: 10000.99099,
            sellAmount: 12000.99099
          },
          {
            isin: 'INF959K01LX2',
            mfLogoUrl: getFundLogoPath(MockFundAmc.NIPPON_LIFE_INDIA),
            fundName: 'Nippon India Growth Fund',
            fundNature: 'Equity',
            fundCategory: 'Mid Cap',
            purchaseMode: 'REGULAR',
            distributionStatus: 'GROWTH',
            currentValue: 18000.99099,
            powerRank: 4,
            powerRating: 'OFF_TRACK',
            taxCategory: 'EQUITY',
            idealAllocationPercentage: { absolute: 2.8, lowerLimit: 1.6, upperLimit: 4.3 },
            buyAmount: 8000.99099,
            sellAmount: null
          },
          {
            isin: 'INF209K01BB2',
            mfLogoUrl: getFundLogoPath(MockFundAmc.SBI),
            fundName: 'SBI Magnum Midcap Fund',
            fundNature: 'Equity',
            fundCategory: 'Mid Cap',
            purchaseMode: 'REGULAR',
            distributionStatus: 'GROWTH',
            currentValue: 9000.99099,
            powerRank: 2,
            powerRating: 'IN_FORM',
            taxCategory: 'EQUITY',
            idealAllocationPercentage: { absolute: 0.9, lowerLimit: 0.5, upperLimit: 2.6 },
            buyAmount: null,
            sellAmount: null
          }
        ]
      },
      {
        categoryId: 'SMALL_CAP',
        categoryName: 'Small Cap',
        idealAllocationPercentage: { absolute: 10.8, lowerLimit: 8.3, upperLimit: 12.4 },
        idealFunds: [
          {
            isin: 'INF209K01YH1',
            mfLogoUrl: getFundLogoPath(MockFundAmc.SBI),
            fundName: 'SBI Small Cap Fund',
            fundNature: 'Equity',
            fundCategory: 'Small Cap',
            purchaseMode: 'REGULAR',
            distributionStatus: 'GROWTH',
            currentValue: 0,
            powerRank: 1,
            powerRating: 'IN_FORM',
            taxCategory: 'EQUITY',
            idealAllocationPercentage: { absolute: 3.5, lowerLimit: 2.6, upperLimit: 4.2 },
            buyAmount: 15000.99099,
            sellAmount: null
          },
          {
            isin: 'INF109K01SC1',
            mfLogoUrl: getFundLogoPath(MockFundAmc.ICICI_PRUDENTIAL),
            fundName: 'ICICI Prudential Smallcap Fund',
            fundNature: 'Equity',
            fundCategory: 'Small Cap',
            purchaseMode: 'REGULAR',
            distributionStatus: 'GROWTH',
            currentValue: 12000.99099,
            powerRank: 3,
            powerRating: 'IN_FORM',
            taxCategory: 'EQUITY',
            idealAllocationPercentage: { absolute: 2.2, lowerLimit: 1.4, upperLimit: 3.6 },
            buyAmount: null,
            sellAmount: 9000.99099
          },
          {
            isin: 'INF200K01SC2',
            mfLogoUrl: getFundLogoPath(MockFundAmc.NIPPON_LIFE_INDIA),
            fundName: 'Nippon India Small Cap Fund',
            fundNature: 'Equity',
            fundCategory: 'Small Cap',
            purchaseMode: 'REGULAR',
            distributionStatus: 'GROWTH',
            currentValue: 0,
            powerRank: 2,
            powerRating: 'IN_FORM',
            taxCategory: 'EQUITY',
            idealAllocationPercentage: { absolute: 1.8, lowerLimit: 1.8, upperLimit: 2.6 },
            buyAmount: 10000.99099,
            sellAmount: null
          }
        ]
      }
    ]
  }

  if (!isTopShape) {
    return idealPlans
  }
  return applyIdealPlansTopShape(idealPlans)
}
